import { Trans } from 'react-i18next';

import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { FinancialText } from '#components/FinancialText';
import type { ReadyFinancialDashboard } from '#components/product-finance/client';
import {
  formatProductDate,
  formatProductMoney,
} from '#components/product-finance/money';

import { SectionCard, SummaryAmount } from './dashboard-shared';
import { summarizePaymentOutlook } from './payment-outlook';

export function PaymentOutlookCard({
  dashboard,
}: {
  dashboard: ReadyFinancialDashboard;
}) {
  const outlook = summarizePaymentOutlook(dashboard);
  const { nextDue } = outlook;
  const currency = dashboard.currentCash.currency;

  return (
    <SectionCard
      title={<Trans>Payment outlook</Trans>}
      tone={outlook.overdueCount > 0 ? 'overdue' : 'normal'}
    >
      <View
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
        }}
      >
        <View style={{ gap: 2 }}>
          <span style={{ color: theme.pageTextSubdued }}>
            <Trans>Next due payment</Trans>
          </span>
          {nextDue === null ? (
            <strong>
              <Trans>No payments due soon.</Trans>
            </strong>
          ) : (
            <>
              <strong>{nextDue.name}</strong>
              <FinancialText as="span">
                {formatProductMoney(nextDue.amount)}
              </FinancialText>
              <span style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
                {formatProductDate(nextDue.date)} ·{' '}
                {nextDue.daysUntilDue === 0 ? (
                  <Trans>Due today</Trans>
                ) : nextDue.daysUntilDue === 1 ? (
                  <Trans>Due tomorrow</Trans>
                ) : (
                  <Trans>Due in {{ days: nextDue.daysUntilDue }} days</Trans>
                )}
              </span>
            </>
          )}
        </View>

        <View style={{ gap: 2 }}>
          <span style={{ color: theme.pageTextSubdued }}>
            <Trans>Overdue payments</Trans>
          </span>
          {outlook.overdueCount === 0 ? (
            <strong>
              <Trans>None</Trans>
            </strong>
          ) : (
            <>
              <FinancialText as="strong" style={{ color: theme.errorText }}>
                {formatProductMoney({
                  minorUnits: outlook.overdueMinorUnits,
                  currency,
                })}
              </FinancialText>
              <span style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
                {outlook.overdueCount} ·{' '}
                {outlook.oldestOverdueDate !== null && (
                  <>
                    <Trans>Oldest</Trans>{' '}
                    {formatProductDate(outlook.oldestOverdueDate)}
                  </>
                )}
              </span>
            </>
          )}
        </View>

        {dashboard.currentCycleOverview !== null && (
          <>
            <SummaryAmount
              label={<Trans>Pending this cycle</Trans>}
              money={dashboard.currentCycleOverview.pendingCommitments}
            />
            <View style={{ gap: 2 }}>
              <span style={{ color: theme.pageTextSubdued }}>
                <Trans>Paid</Trans>
              </span>
              <strong>{outlook.paidCount}</strong>
            </View>
          </>
        )}
      </View>
    </SectionCard>
  );
}
